import { useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { PageContainer } from '../components/PageContainer';
import { UserReviewsList } from '../components/reviews';
import { AvatarEditor } from '../components/AvatarEditor';
import { Avatar } from '../components/Avatar';
import { Button } from '../components/ui/Button';
import { EditIcon } from '../components/ui/icons';
import { ProfileTab } from '../components/profile/ProfileTab';
import { PlaceholderTab } from '../components/profile/PlaceholderTab';
import { PreferencesTab } from '../components/profile/PreferencesTab';
import { SettingsTab } from '../components/profile/SettingsTab';
import { FavoriteVenuesTab } from '../components/profile/FavoriteVenuesTab';
import { MyWines } from '../components/profile/MyWines';
import { trpc } from '../lib/trpc';

type TabId = 'profile' | 'reviews' | 'my-wines' | 'preferences' | 'favorite-venues' | 'settings';

const TABS: { id: TabId; label: string }[] = [
  { id: 'profile', label: 'Profile' },
  { id: 'reviews', label: 'Reviews' },
  { id: 'my-wines', label: 'My Wines' },
  { id: 'preferences', label: 'Preferences' },
  { id: 'favorite-venues', label: 'Favorite Venues' },
  { id: 'settings', label: 'Settings' },
];

export const UserProfile = () => {
  const { user } = useAuth();
  const [isEditingAvatar, setIsEditingAvatar] = useState(false);
  const [activeTab, setActiveTab] = useState<TabId>('profile');

  // Fetch full profile data for the Profile tab
  const { data: profileData } = trpc.user.getMe.useQuery(undefined, {
    enabled: !!user,
  });

  // OAuth users (Google/Apple) always use their provider avatar — no editing allowed
  const isOAuthUser = Boolean(user?.image);

  if (!user) {
    return (
      <div className="px-0 md:px-4 pt-2 pb-4 max-w-4xl mx-auto">
        <PageContainer className="mt-2">
          <div className="text-center py-8">
            <p className="text-gray-600">Please sign in to view your profile.</p>
          </div>
        </PageContainer>
      </div>
    );
  }

  return (
    <div className="px-0 md:px-4 pt-2 pb-4 max-w-4xl mx-auto">
      <PageContainer className="mt-2">
        {/* Profile Header */}
        <div className="border-b border-gray-200 pb-6 mb-0">
          <div className="flex items-center gap-4">
            <div className="relative">
              <Avatar user={user} size="lg" />
              {!isOAuthUser && (
                <button
                  onClick={() => setIsEditingAvatar(!isEditingAvatar)}
                  className="absolute -bottom-0.5 -right-0.5 bg-primary-600 text-white rounded-full p-1 hover:bg-primary-700 transition-colors shadow ring-2 ring-white"
                  title="Edit avatar"
                >
                  <EditIcon className="w-3 h-3" />
                </button>
              )}
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{user.name || 'User'}</h1>
              {user.userName && <p className="text-gray-500 text-sm">@{user.userName}</p>}
              <p className="text-gray-600">{user.email}</p>
            </div>
          </div>

          {/* Avatar Editor Section */}
          {!isOAuthUser && isEditingAvatar && (
            <div className="mt-6 p-6 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Edit Profile Photo</h3>
                <Button variant="ghost" size="sm" onClick={() => setIsEditingAvatar(false)}>
                  Close
                </Button>
              </div>
              <AvatarEditor
                userId={user.id}
                currentAvatarUrl={user.avatar || user.image || undefined}
                onAvatarChange={() => {
                  setIsEditingAvatar(false);
                }}
                onCancel={() => setIsEditingAvatar(false)}
              />
            </div>
          )}
        </div>

        {/* Tab Navigation — horizontally scrollable on mobile */}
        <nav
          className="flex overflow-x-auto scrollbar-hide -mb-px"
          style={{ scrollSnapType: 'x mandatory' }}
        >
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{ scrollSnapAlign: 'start' }}
              className={`flex-shrink-0 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>

        {/* Tab Content */}
        <div className="pt-6">
          {activeTab === 'profile' && profileData && (
            <ProfileTab
              profile={{
                name: profileData.name || null,
                bio: profileData.bio,
                birthday: profileData.birthday,
                homeLocation: profileData.homeLocation,
                address: profileData.address,
                publicProfile: profileData.publicProfile,
                privacySettings: profileData.privacySettings,
              }}
              email={user.email}
            />
          )}

          {activeTab === 'reviews' && <UserReviewsList />}

          {activeTab === 'my-wines' && <MyWines />}

          {activeTab === 'preferences' && profileData && (
            <PreferencesTab
              preferences={{
                winePreferences: profileData.winePreferences,
                wineDislikes: profileData.wineDislikes,
                regionPreference: profileData.regionPreference,
                venuePreferences: profileData.venuePreferences,
              }}
            />
          )}

          {activeTab === 'favorite-venues' && <FavoriteVenuesTab />}

          {activeTab === 'settings' && profileData && (
            <SettingsTab
              hasPassword={profileData.hasPassword}
              phoneNumber={profileData.phoneNumber}
              phoneVerified={profileData.phoneVerified}
              distanceUnit={profileData.distanceUnit}
            />
          )}
        </div>
      </PageContainer>
    </div>
  );
};
